import { splitComposedDocChatQuery } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatTagPicks'
import {
  ArtifactKindSchema,
  DocChatLotoReportSchema,
  DocChatMetadataSchema,
  DocChatSseErrorEventSchema,
  DocChatSseThoughtEventSchema,
  DocChatTimeSeriesDataSchema,
  ResponseSegmentSchema,
  RevisionResultSchema,
  SearchResultDocumentSchema,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { InspectionResultSchema } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import type {
  DocChatConversation,
  DocChatEvent,
} from 'features/knowledgeDocumentViewer/types'
import { z } from 'zod'

const documentChatAgentUserTurnSchema = z.object({
  role: z.literal('user'),
  schema_version: z.number().int().positive(),
  query: z.string().min(1),
})

const documentChatAgentModelOutputSchema = z.object({
  final_response: z.string().min(1),
})

const documentChatAgentModelTurnSchema = z.object({
  role: z.literal('model'),
  model_output: documentChatAgentModelOutputSchema,
})

export type DocumentChatStoredTurn = {
  finalResponse?: string
  query: string
}

function agentTurns(data: unknown): unknown[] | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const turns = 'turns' in data ? data.turns : undefined
  return Array.isArray(turns) ? turns : null
}

/**
 * User prompts from untrusted agent JSON, with `finalResponse` when the
 * following model turn already finished. Extra fields are ignored.
 */
export function storedTurnsFromAgentTurns(
  data: unknown
): DocumentChatStoredTurn[] {
  const turns = agentTurns(data)
  if (!turns || turns.length === 0) return []
  const stored: DocumentChatStoredTurn[] = []
  let pendingQuery: string | undefined
  turns.forEach((turn) => {
    const user = documentChatAgentUserTurnSchema.safeParse(turn)
    if (user.success) {
      // Stored agent prompts are composed queries; never render pick context.
      const query = splitComposedDocChatQuery(user.data.query.trim()).query
      if (query.length === 0) return
      if (pendingQuery !== undefined) stored.push({ query: pendingQuery })
      pendingQuery = query
      return
    }
    const model = documentChatAgentModelTurnSchema.safeParse(turn)
    const finalResponse = model.success
      ? model.data.model_output.final_response.trim()
      : ''
    if (pendingQuery === undefined || finalResponse.length === 0) return
    stored.push({ query: pendingQuery, finalResponse })
    pendingQuery = undefined
  })
  if (pendingQuery !== undefined) stored.push({ query: pendingQuery })
  return stored
}

export function pendingUserQueryFromAgentTurns(data: unknown): string | null {
  return storedTurnsFromAgentTurns(data).at(-1)?.query ?? null
}

export const DOCUMENT_CHAT_SNAPSHOT_SCHEMA_VERSION = 1
export const DOCUMENT_CHAT_CONVERSATION_FILE_PATH = 'conversation.json'
export const DOCUMENT_CHAT_SESSION_PAGE_SIZE = 20
export const DOCUMENT_CHAT_STORED_TURN_ID_PREFIX = 'entry-turn-'
export const DOCUMENT_CHAT_UI_HISTORY_SCHEMA_VERSION = 1
export const DOCUMENT_CHAT_USER_TAG = 'doc-chat-user'

export const documentChatActiveTurnSchema = z.object({
  schema_version: z.number().int().positive(),
  id: z.string().min(1),
  status: z.enum(['running', 'completed', 'failed', 'cancelled']),
  error: z.string().nullish(),
})

export type DocumentChatActiveTurn = z.infer<
  typeof documentChatActiveTurnSchema
>

export type DocumentChatRestoreMode =
  | 'snapshot'
  | 'running'
  | 'failed'
  | 'cancelled'
  | 'prompt'

export function documentChatRestoreMode(session: {
  activeTurn?: DocumentChatActiveTurn
  snapshotPath?: string
}): DocumentChatRestoreMode {
  if (session.activeTurn?.status === 'running') return 'running'
  if (session.activeTurn?.status === 'failed') return 'failed'
  if (session.activeTurn?.status === 'cancelled') return 'cancelled'
  if (session.snapshotPath === DOCUMENT_CHAT_CONVERSATION_FILE_PATH) {
    return 'snapshot'
  }
  return 'prompt'
}

export const documentChatUiHistorySchema = z.object({
  schema_version: z.number().int().positive(),
  title: z.string().nullable().optional(),
  visible_turn_count: z.number().int().nonnegative().nullable().optional(),
  snapshot_path: z.string().nullable().optional(),
  last_completed_turn_id: z.string().nullable().optional(),
  last_completed_at: z.number().int().nullable().optional(),
  archived: z.boolean().optional(),
  archived_at: z.number().int().nullable().optional(),
  deleted: z.boolean().optional(),
  deleted_at: z.number().int().nullable().optional(),
  pinned: z.boolean().optional(),
  pinned_at: z.number().int().nullable().optional(),
})

const persistedSubmittedQueryEventSchema = z.strictObject({
  id: z.string(),
  type: z.literal('submitted_query'),
  query: z.string(),
})

const persistedThinkingEventSchema = z.strictObject({
  id: z.string(),
  type: z.literal('thinking'),
  query: z.string(),
})

const persistedCancelledEventSchema = z.strictObject({
  id: z.string(),
  type: z.literal('cancelled'),
  query: z.string(),
})

const persistedThoughtEventSchema = DocChatSseThoughtEventSchema.extend({
  id: z.string(),
})

const persistedErrorEventSchema = DocChatSseErrorEventSchema.extend({
  id: z.string(),
})

const persistedResponseEventSchema = z.looseObject({
  id: z.string(),
  type: z.literal('response'),
  query: z.string(),
  site_id: z.string(),
  conversation_id: z.string(),
  metadata: DocChatMetadataSchema,
  results: z.array(SearchResultDocumentSchema).optional(),
  resultCount: z.number().int().nonnegative().optional(),
  final_response: z.string().optional(),
  response: z.string().optional(),
  response_segments: z.array(ResponseSegmentSchema).optional(),
  time_series: DocChatTimeSeriesDataSchema.optional(),
  revision_results: z.array(RevisionResultSchema).optional(),
  loto_report: DocChatLotoReportSchema,
  inspection_results: z.array(InspectionResultSchema).optional(),
})

const persistedArtifactEventSchema = z.strictObject({
  id: z.string(),
  type: z.literal('artifact'),
  artifact_id: z.string(),
  kind: ArtifactKindSchema,
  title: z.string(),
  revision: z.number().int(),
  created: z.boolean().default(false),
  missing_count: z.number().int().nonnegative(),
  changed_paths: z.array(z.string()),
})

const persistedDocChatEventSchema = z.discriminatedUnion('type', [
  persistedSubmittedQueryEventSchema,
  persistedThinkingEventSchema,
  persistedCancelledEventSchema,
  persistedThoughtEventSchema,
  persistedErrorEventSchema,
  persistedResponseEventSchema,
  persistedArtifactEventSchema,
])

const snapshotTurnSchema = z.strictObject({
  id: z.string(),
  query: z.string(),
  hiddenContext: z.string().optional(),
  tagSelections: z
    .array(
      z.strictObject({
        identifier: z.string(),
        entryId: z.string(),
        label: z.string(),
      })
    )
    .optional(),
  events: z.array(persistedDocChatEventSchema),
})

export const documentChatConversationSnapshotSchema = z.strictObject({
  schema_version: z.literal(DOCUMENT_CHAT_SNAPSHOT_SCHEMA_VERSION),
  title: z.string(),
  conversation: z.strictObject({
    id: z.string(),
    pendingSessionKey: z.string().optional(),
    activeTurnId: z.string().optional(),
    turns: z.array(snapshotTurnSchema),
  }),
})

export type DocumentChatConversationSnapshot = z.infer<
  typeof documentChatConversationSnapshotSchema
>

/**
 * Saved files also keep a backend `agent` blob. Hydrate only the UI conversation.
 */
export function parseDocumentChatConversationSnapshot(
  value: unknown
): DocumentChatConversationSnapshot | null {
  const envelope =
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'schema_version' in value &&
    'title' in value &&
    'conversation' in value
      ? {
          schema_version: value.schema_version,
          title: value.title,
          conversation: value.conversation,
        }
      : value
  const parsed = documentChatConversationSnapshotSchema.safeParse(envelope)
  return parsed.success ? parsed.data : null
}

function docChatEventFromSnapshot(
  event: z.infer<typeof persistedDocChatEventSchema>
): DocChatEvent {
  if (event.type !== 'response') return event
  return {
    id: event.id,
    type: 'response',
    query: event.query,
    site_id: event.site_id,
    conversation_id: event.conversation_id,
    metadata: event.metadata,
    resultCount: event.resultCount ?? event.results?.length ?? 0,
    ...(event.results === undefined ? {} : { results: event.results }),
    ...(event.final_response === undefined
      ? {}
      : { final_response: event.final_response }),
    ...(event.response === undefined ? {} : { response: event.response }),
    ...(event.response_segments === undefined
      ? {}
      : { response_segments: event.response_segments }),
    ...(event.time_series === undefined
      ? {}
      : { time_series: event.time_series }),
    ...(event.revision_results === undefined
      ? {}
      : { revision_results: event.revision_results }),
    ...(event.loto_report === undefined
      ? {}
      : { loto_report: event.loto_report }),
    ...(event.inspection_results === undefined
      ? {}
      : { inspection_results: event.inspection_results }),
  }
}

type SnapshotTurn =
  DocumentChatConversationSnapshot['conversation']['turns'][number]

/** Backend-written turns store the composed prompt; peel pick context back off. */
function splitSnapshotTurnQuery(
  turn: SnapshotTurn
): Pick<SnapshotTurn, 'query' | 'hiddenContext'> {
  if (turn.hiddenContext !== undefined) {
    return { query: turn.query, hiddenContext: turn.hiddenContext }
  }
  return splitComposedDocChatQuery(turn.query)
}

function snapshotTurnEvents(turn: SnapshotTurn, query: string): DocChatEvent[] {
  return turn.events.map((event) =>
    event.type === 'submitted_query' && event.query === turn.query
      ? { ...event, query }
      : docChatEventFromSnapshot(event)
  )
}

export function conversationFromSnapshot(
  snapshot: DocumentChatConversationSnapshot
): DocChatConversation {
  return {
    id: snapshot.conversation.id,
    pendingSessionKey: snapshot.conversation.pendingSessionKey,
    activeTurnId: snapshot.conversation.activeTurnId,
    turns: snapshot.conversation.turns.map((turn) => {
      const { query, hiddenContext } = splitSnapshotTurnQuery(turn)
      return {
        id: turn.id,
        query,
        ...(hiddenContext === undefined ? {} : { hiddenContext }),
        ...(turn.tagSelections === undefined
          ? {}
          : { tagSelections: turn.tagSelections }),
        events: snapshotTurnEvents(turn, query),
      }
    }),
  }
}
